/** Редактор планов месяца (вкладка «План», право `mp_plans_edit`): сетка площадка × юрлицо ×
 *  показатель, сохранение по ячейке на blur/Enter (`PUT /api/mp-plans`; пусто — удалить план),
 *  копирование из прошлого месяца в пустой месяц. Ошибки сервера — текстом `detail` как есть. */
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";

import {
  copyPlans,
  putPlan,
  type PlanItem,
  type PlanLegalsMp,
  type PlanMetric,
  type PlanMp,
} from "@/api/mpPlans";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { fmtWhen, monthTitle } from "./format";
import { MP_CONFIGS } from "./mp";
import { isRecentLegal, parsePlanInput, planInputText, shiftMonth } from "./planCalc";
import { PLAN_KEYS, usePlanLegals, usePlans } from "./queries";
import { Loading } from "./shared";
import { apiErrorText, thClass } from "./ui";

const ERR_BOX =
  "rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400";

function useInvalidatePlans(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: PLAN_KEYS.planFact }),
      qc.invalidateQueries({ queryKey: PLAN_KEYS.list }),
    ]);
  };
}

export function PlanEditor({ month }: { month: string }): React.ReactElement {
  const year = Number(month.slice(0, 4));
  const mon = Number(month.slice(5, 7));
  const plans = usePlans(year, mon);
  const legals = usePlanLegals();
  const [showAll, setShowAll] = useState(false);
  // сообщение о копировании живёт здесь: после invalidate кнопка пропадает (планы появились),
  // а «скопировано N» должно остаться видимым
  const [copyMsg, setCopyMsg] = useState<CopyMsg | null>(null);

  return (
    <div className="space-y-3 rounded-md border bg-card p-3" data-testid="plan-editor">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Планы на {monthTitle(month).toLowerCase()}, ₽</h2>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Показать все юрлица
        </label>
        {plans.data && plans.data.items.length === 0 && <CopyButton year={year} month={mon} onMsg={setCopyMsg} />}
        {copyMsg && (
          <span
            role={copyMsg.ok ? "status" : "alert"}
            data-testid="plan-copy-msg"
            className={cn(
              "text-xs",
              copyMsg.ok ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400",
            )}
          >
            {copyMsg.text}
          </span>
        )}
      </div>
      {(plans.isLoading || legals.isLoading) && <Loading text="Загружаю планы и юрлица…" />}
      {plans.isError && (
        <div role="alert" className={ERR_BOX}>
          Не удалось получить планы. {apiErrorText(plans.error, "площадки")}
        </div>
      )}
      {legals.isError && (
        <div role="alert" className={ERR_BOX}>
          Не удалось получить юрлица витрин. {apiErrorText(legals.error, "площадки")}
        </div>
      )}
      {plans.data && legals.data && (
        <div className="overflow-x-auto">
          <table className="w-auto border-collapse text-sm">
            <thead>
              <tr className="border-b">
                <th className={cn(thClass, "text-left")}>Площадка / юрлицо</th>
                {plans.data.metrics.map((m) => (
                  <th key={m.code} className={thClass}>
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {legals.data.mps.map((lm) => (
                <MpRows
                  key={lm.mp}
                  lm={lm}
                  items={plans.data.items.filter((i) => i.mp === lm.mp)}
                  metrics={plans.data.metrics}
                  month={month}
                  showAll={showAll}
                />
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">
            Сохраняется при выходе из поля или Enter; пустое поле — план удаляется; Esc — отменить ввод.
            Скрыты юрлица без данных за 60 дней до начала месяца и без плана в нём.
          </p>
        </div>
      )}
    </div>
  );
}

interface CopyMsg {
  ok: boolean;
  text: string;
}

function CopyButton({
  year,
  month,
  onMsg,
}: {
  year: number;
  month: number;
  onMsg: (m: CopyMsg | null) => void;
}): React.ReactElement {
  const invalidate = useInvalidatePlans();
  const [busy, setBusy] = useState(false);
  const prev = shiftMonth(`${year}-${String(month).padStart(2, "0")}`, -1);
  return (
    <span className="flex items-center gap-2">
      <button
        type="button"
        disabled={busy}
        className="h-8 rounded-md border bg-background px-3 text-xs hover:bg-accent disabled:opacity-50"
        onClick={async () => {
          setBusy(true);
          onMsg(null);
          try {
            const r = await copyPlans(year, month);
            onMsg({ ok: true, text: `скопировано ${r.copied}` });
            await invalidate();
          } catch (err) {
            onMsg({ ok: false, text: extractErrorMessage(err, "ошибка запроса") });
          } finally {
            setBusy(false);
          }
        }}
        title={`Скопировать все планы из ${monthTitle(prev).toLowerCase()}`}
      >
        Скопировать из прошлого месяца
      </button>
    </span>
  );
}

function MpRows({
  lm,
  items,
  metrics,
  month,
  showAll,
}: {
  lm: PlanLegalsMp;
  items: PlanItem[];
  metrics: PlanMetric[];
  month: string;
  showAll: boolean;
}): React.ReactElement {
  const label = MP_CONFIGS[lm.mp].label;
  const span = metrics.length + 1;
  const withPlan = [...new Set(items.map((i) => i.legal_name))];
  const known = "legals" in lm ? lm.legals.filter((l) => l.legal_name !== null) : [];
  const knownNames = known.map((l) => l.legal_name as string);
  const shown = known
    .filter((l) => showAll || withPlan.includes(l.legal_name!) || isRecentLegal(l.date_to, month))
    .map((l) => l.legal_name as string);
  // план юрлица, которого нет в витрине (пропало, или витрина недоступна) — показываем, чтобы
  // его можно было увидеть и удалить
  const orphans = withPlan.filter((n) => !knownNames.includes(n));
  const hidden = known.length - shown.length;
  return (
    <>
      <tr className="border-t bg-muted/40">
        <td colSpan={span} className="px-2 py-1.5 font-semibold">
          {label}
          {hidden > 0 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">скрыто юрлиц: {hidden}</span>
          )}
        </td>
      </tr>
      {"error" in lm && (
        <tr>
          <td colSpan={span} className="px-2 py-1.5">
            <div role="alert" className={ERR_BOX} data-testid={`plan-legals-error-${lm.mp}`}>
              Юрлица {label} недоступны: {lm.error}
            </div>
          </td>
        </tr>
      )}
      {[...shown, ...orphans].map((legal) => (
        <tr key={legal} className="border-t border-border/50">
          <td className="px-2 py-1 pl-5 whitespace-nowrap">
            {legal}
            {orphans.includes(legal) && "legals" in lm && (
              <span className="ml-2 text-[11px] text-amber-700 dark:text-amber-400">нет в витрине</span>
            )}
          </td>
          {metrics.map((m) => (
            <td key={m.code} className="px-2 py-1 align-top">
              <PlanInput
                month={month}
                mp={lm.mp}
                legal={legal}
                metric={m}
                mpLabel={label}
                item={items.find((i) => i.legal_name === legal && i.metric === m.code) ?? null}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

type CellStatus =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; text: string };

function PlanInput({
  month,
  mp,
  legal,
  metric,
  mpLabel,
  item,
}: {
  month: string;
  mp: PlanMp;
  legal: string;
  metric: PlanMetric;
  mpLabel: string;
  item: PlanItem | null;
}): React.ReactElement {
  const invalidate = useInvalidatePlans();
  const server = item?.value ?? null;
  const [draft, setDraft] = useState(planInputText(server));
  const [status, setStatus] = useState<CellStatus>({ kind: "idle" });
  const focused = useRef(false);
  const cancelled = useRef(false);
  // последнее значение, подтверждённое ответом PUT: список планов обновляется refetch-ем с задержкой,
  // и до него `server` устарел (undefined — подтверждений не было, база — список)
  const confirmed = useRef<number | null | undefined>(undefined);
  const base = (): number | null => (confirmed.current !== undefined ? confirmed.current : server);

  // значение с сервера поменялось (сохранили, скопировали) — показываем его, если поле не правят
  useEffect(() => {
    confirmed.current = undefined;
    if (!focused.current) setDraft(planInputText(server));
  }, [server]);

  const save = async (): Promise<void> => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const value = parsePlanInput(draft);
    if (value === undefined) {
      setStatus({ kind: "error", text: `не число: «${draft}»` });
      return;
    }
    if (value === base()) {
      setStatus({ kind: "idle" });
      setDraft(planInputText(base()));
      return;
    }
    setStatus({ kind: "saving" });
    try {
      const res = await putPlan({
        year: Number(month.slice(0, 4)),
        month: Number(month.slice(5, 7)),
        mp,
        legal_name: legal,
        metric: metric.code,
        value,
      });
      confirmed.current = "deleted" in res ? null : res.value;
      setStatus({ kind: "saved" });
      await invalidate();
    } catch (err) {
      setStatus({ kind: "error", text: extractErrorMessage(err, "ошибка запроса") });
    }
  };

  const title = item
    ? `изменил ${item.updated_by ?? "неизвестно кто"}, ${fmtWhen(item.updated_at)}`
    : "плана нет";
  return (
    <div className="w-40">
      <input
        type="text"
        inputMode="decimal"
        aria-label={`${mpLabel} · ${legal} · ${metric.label}`}
        title={title}
        value={draft}
        disabled={status.kind === "saving"}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            cancelled.current = true;
            setDraft(planInputText(base()));
            setStatus({ kind: "idle" });
            e.currentTarget.blur();
          }
        }}
        onBlur={() => {
          focused.current = false;
          void save();
        }}
        className={cn(
          "h-8 w-full rounded-md border bg-background px-2 text-right text-sm tabular-nums",
          status.kind === "error" && "border-red-400 dark:border-red-700",
        )}
      />
      {status.kind === "saving" && <div className="text-[11px] text-muted-foreground">сохраняю…</div>}
      {status.kind === "saved" && (
        <div className="text-[11px] text-emerald-700 dark:text-emerald-400">✓ сохранено</div>
      )}
      {status.kind === "error" && (
        <div role="alert" className="text-[11px] leading-tight text-red-600 dark:text-red-400">
          {status.text}
        </div>
      )}
    </div>
  );
}
